import { gql, type TypedDocumentNode } from '@apollo/client';
import type { CSSProperties } from 'react';

import { StepState } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Target } from '@sorare/core/src/atoms/icons/Target';
import { iconFire } from '@sorare/core/src/atoms/icons/system/iconFire.ts';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandM,
  BrandS,
  BrandT,
  BrandXS,
} from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { isType } from '@sorare/core/src/lib/gql';
import { css, keyframes } from '@sorare/css';

import type {
  ThresholdStep_careerProStep,
  ThresholdStep_originalRewardConfig,
  ThresholdStep_rewardConfig,
} from './__generated__/index.graphql.ts';

export const isLocked = (step: { state: StepState }) =>
  step.state === StepState.LOCKED ||
  step.state === StepState.PRE_MATCHDAY_LOCKED;

const tailColorStyle = css`
  background: var(--tail-color, var(--c-score-low));
`;
const levelNumberShadow = css`
  text-shadow: 0 -4px 6px var(--c-black);
`;
const activeLevelNumberShadow = css`
  text-shadow:
    0 0 6px var(--c-rare),
    0 -4px 6px var(--c-black);
`;
const tileWidth = css`
  width: 220px;
`;
const tileOpacity = css`
  opacity: var(--tile-opacity, 1);
`;
const activeTileGlow = css`
  filter: drop-shadow(
    0 0 6px color-mix(in srgb, var(--active-color) 50%, transparent)
  );
`;

const STAGGER_MS = 130;
const CONTENT_OFFSET_MS = 150;

const fillGrow = keyframes`
  from {
    transform: translateX(-50%) scaleY(0);
  }
  to {
    transform: translateX(-50%) scaleY(1);
  }
`;

const flamePopFrames = keyframes`
  0% {
    transform: scale(0.4) translateY(3px);
  }
  55% {
    transform: scale(1.3) translateY(-2px);
  }
  100% {
    transform: scale(1) translateY(0);
  }
`;

const flameIgniteFrames = keyframes`
  0% {
    opacity: 0;
  }
  55% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
`;

const tileScaleFrames = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-16px) scale(0.92);
  }
  60% {
    opacity: var(--tile-opacity, 1);
    transform: translateX(0) scale(1.04);
  }
  100% {
    opacity: var(--tile-opacity, 1);
    transform: translateX(0) scale(1);
  }
`;

const rewardTextFrames = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.7);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const rewardImageFrames = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.2) rotate(-18deg);
  }
  60% {
    opacity: var(--tile-opacity, 1);
    transform: scale(1.25) rotate(6deg);
  }
  100% {
    opacity: var(--tile-opacity, 1);
    transform: scale(1) rotate(0deg);
  }
`;

const orangeFillEnter = css`
  transform: translateX(-50%);
  transform-origin: center bottom;
  box-shadow: 0 0 8px 1px var(--tail-glow, rgba(var(--c-rgb-score-low), 0.5));
  animation: ${fillGrow} 0.5s var(--ease-out-expo) both;
  animation-delay: var(--enter-delay, 0ms);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const flamePop = css`
  animation: ${flamePopFrames} 0.7s var(--ease-out-back) both;
  animation-delay: var(--enter-delay, 0ms);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const flameIgnite = css`
  animation: ${flameIgniteFrames} 0.7s var(--ease-out-back) both;
  animation-delay: var(--enter-delay, 0ms);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const tileScale = css`
  animation: ${tileScaleFrames} 0.7s var(--ease-out-back) both;
  animation-delay: var(--enter-delay, 0ms);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const rewardTextReveal = css`
  animation: ${rewardTextFrames} 0.7s var(--ease-out-back) both;
  animation-delay: var(--enter-delay, 0ms);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const rewardImageReveal = css`
  animation: ${rewardImageFrames} 0.75s var(--ease-out-back) both;
  animation-delay: var(--enter-delay, 0ms);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

type Props = {
  /** The number shown inside the flame. */
  level: number;
  target: number;
  locked?: boolean;
  active?: boolean;
  animationOrder: number;
  rewardConfigs: ThresholdStep_rewardConfig[];
  originalRewardConfigs?: ThresholdStep_originalRewardConfig[] | null;
  /** Connector drawn below the flame. Defaults to the reached (orange) color. */
  tailColor?: string;
  activeColor?: string;
  rewardTextColor?: string;
  dimmed?: boolean;
  /** Drops the connector, e.g. on the bottom-most step of a ladder. */
  hideTail?: boolean;
};

export const ThresholdStep = ({
  level,
  target,
  locked,
  active,
  animationOrder,
  rewardConfigs,
  originalRewardConfigs,
  tailColor,
  hideTail,
  activeColor = 'var(--c-arena-primary)',
  rewardTextColor = 'var(--c-gradient-greenYellow)',
  dimmed,
}: Props) => {
  const fillDelayMs = animationOrder * STAGGER_MS;
  const contentDelayMs = fillDelayMs + CONTENT_OFFSET_MS;
  const reward = rewardConfigs[0];
  const originalReward = reward
    ? originalRewardConfigs?.find(
        ({ __typename }) => __typename === reward.__typename
      )
    : null;

  const showReward = !!reward && !isType(reward, 'MonetaryRewardConfig');

  return (
    <div
      className="relative flex items-center gap-4"
      style={{ '--tile-opacity': dimmed ? 0.5 : undefined } as CSSProperties}
    >
      <div
        className="relative size-4 shrink-0"
        style={
          {
            '--tail-color': tailColor,
            '--tail-glow': tailColor,
          } as CSSProperties
        }
      >
        {!hideTail && (
          <div
            className={`absolute top-1/2 left-1/2 h-9.5 w-0.5 ${tailColorStyle} ${orangeFillEnter}`}
            style={{ '--enter-delay': `${fillDelayMs}ms` } as CSSProperties}
          />
        )}
        {!hideTail && !!active && (
          <div
            className={`absolute top-1/2 left-1/2 h-22 w-0.5 ${tailColorStyle} ${orangeFillEnter}`}
            style={{ '--enter-delay': `${fillDelayMs}ms` } as CSSProperties}
          />
        )}
        <span
          className={`relative block ${flamePop}`}
          style={{ '--enter-delay': `${contentDelayMs}ms` } as CSSProperties}
        >
          <FontAwesomeIcon
            icon={iconFire}
            width={32}
            height={32}
            fontSize={32}
            color="var(--c-nd-50-opaque)"
          />
          <span
            className={`absolute inset-0 block ${flameIgnite}`}
            style={{ '--enter-delay': `${contentDelayMs}ms` } as CSSProperties}
          >
            <FontAwesomeIcon
              icon={iconFire}
              width={32}
              height={32}
              fontSize={32}
              gradient={{
                direction: { x1: '80%', y1: '110%', x2: '34%', y2: '62%' },
                stops: [
                  { offset: '0%', color: '#FEF314' },
                  { offset: '100%', color: '#FF4E22' },
                ],
              }}
            />
          </span>
        </span>
        <BrandS
          className={`absolute inset-0 grid place-items-center ${
            active ? activeLevelNumberShadow : levelNumberShadow
          }`}
          color="var(--c-white)"
        >
          {level}
        </BrandS>
      </div>
      <Bezel
        className={`h-7 shrink-0 ${tileWidth} ${tileScale} ${tileOpacity} ${active ? activeTileGlow : ''}`}
        style={
          {
            '--enter-delay': `${contentDelayMs}ms`,
            '--active-color': activeColor,
          } as CSSProperties
        }
        background="var(--c-nl-500)"
        borderColor={active ? activeColor : 'var(--c-nd-100)'}
      >
        <div
          className={`flex h-full items-center justify-between gap-1 pl-2 ${showReward ? 'pr-5' : 'pr-2.5'}`}
        >
          <Horizontal gap={0.5}>
            {locked ? (
              <FontAwesomeIcon
                icon={iconLock}
                width={16}
                height={16}
                color="var(--c-nd-600)"
              />
            ) : (
              <Target size={2} />
            )}
            <BrandT color="var(--c-white)">{target}</BrandT>
          </Horizontal>
          {!!reward && (
            <div
              className={`flex flex-col items-end gap-1 ${rewardTextReveal}`}
              style={
                { '--enter-delay': `${contentDelayMs}ms` } as CSSProperties
              }
            >
              {!!originalReward && (
                <BrandXS className="line-through" color="var(--c-nd-600)">
                  <RewardConfigText
                    anyRewardConfig={originalReward}
                    disableMonetaryRewardDisclaimer
                  />
                </BrandXS>
              )}
              <BrandM
                className="text-gradient-color"
                style={
                  { '--text-gradient-color': rewardTextColor } as CSSProperties
                }
              >
                <RewardConfigText anyRewardConfig={reward} />
              </BrandM>
            </div>
          )}
        </div>
      </Bezel>
      {!!showReward && (
        <div
          className={`absolute -top-1.5 -right-2.5 ${rewardImageReveal} ${tileOpacity}`}
          style={{ '--enter-delay': `${contentDelayMs}ms` } as CSSProperties}
        >
          <RewardConfigImage anyRewardConfig={reward} size={8} />
        </div>
      )}
    </div>
  );
};

const rewardConfig = gql`
  fragment ThresholdStep_rewardConfig on AnyRewardConfigInterface {
    id
    ...RewardConfigImage_anyRewardConfigInterface
    ...RewardConfigText_anyRewardConfigInterface
  }
  ${RewardConfigImage.fragments.anyRewardConfigInterface}
  ${RewardConfigText.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<ThresholdStep_rewardConfig>;

const originalRewardConfig = gql`
  fragment ThresholdStep_originalRewardConfig on AnyRewardConfigInterface {
    id
    ...RewardConfigText_anyRewardConfigInterface
  }
  ${RewardConfigText.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<ThresholdStep_originalRewardConfig>;

ThresholdStep.fragments = {
  rewardConfig,
  originalRewardConfig,
  careerProStep: gql`
    fragment ThresholdStep_careerProStep on CareerProStep {
      id
      level
      state
      target
      rewardConfigs {
        id
        ...ThresholdStep_rewardConfig
      }
      originalRewardConfigs {
        id
        ...ThresholdStep_originalRewardConfig
      }
    }
    ${rewardConfig}
    ${originalRewardConfig}
  ` as TypedDocumentNode<ThresholdStep_careerProStep>,
};
